import { useCallback, useEffect, useMemo, useState } from "react";
import { MenuItem, Stack, TextField, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";
import { AbsenceContainer, AbsenceTableCard } from "./Absence.styles";
import AbsenceTable from "./AbsenceTable";
import AbsenceCalendar from "./AbsenceCalendar";
import { absenceApi } from "../../../api/absenceApi";
import { employeesApi } from "../../../api/employeesApi";
import PageTitle from "../../../components/common/PageTitle/PageTitle";
import { AbsenceEntry, AbsenceApprovalStatus } from "../../../types/absence";
import { EmployeeUserType } from "../../../types/employeeType";
import { GeneralButton } from "../../../common/ui/GeneralButton";
import { PaginationParameters, PaginationResult } from "../../../api/Pagination";
import { AppDispatch } from "../../../store/store";
import { handleRequest } from "../../../store/utils/handleRequest";
import { useServerSidePagination } from "../../../hooks/PaginationHooks";
import { useAppDispatch } from "../../../hooks/StoreHooks";
import { createAsyncThunk } from "@reduxjs/toolkit";

const breadcrumbs = [
  {
    title: "absence.breadcrumb",
    link: "absence",
  },
];

export type AbsenceFilters = {
  employeeUserId: string;
  startDate: Dayjs | null;
  endDate: Dayjs | null;
};

const createDefaultFilters = (employeeUserId: string): AbsenceFilters => ({
  employeeUserId,
  startDate: null,
  endDate: null,
});

type AbsencePageProps = {
  serviceModuleId: number;
  hidePageTitle?: boolean;
  fixedEmployeeId?: string;
  hideEmployeeFilter?: boolean;
  hideCalendar?: boolean;
};

const AbsencePage = ({
  hidePageTitle = false,
  serviceModuleId,
  fixedEmployeeId,
  hideEmployeeFilter = false,
  hideCalendar = false,
}: AbsencePageProps) => {
  const { t, i18n } = useTranslation();
  const dateFormat = i18n.language === "de" ? "DD.MM.YYYY" : "MM/DD/YYYY";
  const dispatch = useAppDispatch();
  const [employees, setEmployees] = useState<EmployeeUserType[]>([]);
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(false);
  const [updatingId, setUpdatingId] = useState<number | null>(null);
  const [filters, setFilters] = useState<AbsenceFilters>(() =>
    createDefaultFilters(fixedEmployeeId ?? "all")
  );

  useEffect(() => {
    if (fixedEmployeeId) {
      setFilters(createDefaultFilters(fixedEmployeeId));
    }
  }, [fixedEmployeeId]);

  const paginatedAbsences = useServerSidePagination<AbsenceEntry>(
    async (paginationParameters) => {
      const result = await dispatch(
        fetchPaginatedAbsencesThunk({
          serviceModuleId,
          ...paginationParameters,
          employeeUserId: fixedEmployeeId
            ? fixedEmployeeId
            : filters.employeeUserId !== "all"
              ? filters.employeeUserId
              : undefined,
          startDate: filters.startDate ? filters.startDate.format("YYYY-MM-DD") : undefined,
          endDate: filters.endDate ? filters.endDate.format("YYYY-MM-DD") : undefined,
          order: paginationParameters.order ?? undefined,
        })
      );

      if (fetchPaginatedAbsencesThunk.fulfilled.match(result)) {
        return result.payload;
      }

      return { items: [], totalCount: 0 };
    },
    [filters]
  );

  const loadEmployees = useCallback(async () => {
    setIsLoadingEmployees(true);
    const response = await employeesApi.getServiceModuleEmployees(serviceModuleId);
    if (response.success() && response.result) {
      setEmployees(response.result);
    }
    setIsLoadingEmployees(false);
  }, []);

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  const handleUpdateStatus = useCallback(
    async (absence: AbsenceEntry, status: AbsenceApprovalStatus) => {
      if (absence.absenceApproval === status) return;
      setUpdatingId(absence.id);

      const response = await absenceApi.updateAbsence(serviceModuleId, absence.id, status);

      if (response.success() && response.result) {
        paginatedAbsences.updateTableItems();
      }

      setUpdatingId(null);
    },
    [paginatedAbsences, serviceModuleId]
  );

  const employeeNameMap = useMemo(() => {
    const map = new Map<string, string>();

    employees.forEach((employee) => {
      if (!employee.id) {
        return;
      }
      const label = `${employee.firstName ?? ""} ${employee.lastName ?? ""}`.trim();
      map.set(employee.id, label || employee.id);
    });

    paginatedAbsences.items.forEach((absence) => {
      const fallbackId = absence.employeeUserId || absence.employeeUser?.id;
      if (!fallbackId) {
        return;
      }

      const firstName = absence.employeeUser?.firstName ?? "";
      const lastName = absence.employeeUser?.lastName ?? "";
      const fullName = `${firstName} ${lastName}`.trim();

      if (fullName) {
        map.set(fallbackId, fullName);
      }
    });

    return map;
  }, [paginatedAbsences.items, employees]);

  const employeeFilterOptions = useMemo(() => {
    return Array.from(employeeNameMap.entries())
      .filter(([id]) => !!id)
      .map(([id, label]) => ({ id, label }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));
  }, [employeeNameMap]);

  const getEmployeeName = useCallback(
    (employeeId: string) => {
      if (!employeeId) {
        return t("serviceModulesPage.absencePage.unknown");
      }

      const mappedName = employeeNameMap.get(employeeId);
      if (mappedName) {
        return mappedName;
      }

      return employeeId;
    },
    [employeeNameMap, t]
  );

  const isTableLoading = paginatedAbsences.isLoading ||
    (isLoadingEmployees && employees.length === 0);

  const handleFiltersChange = useCallback((partial: Partial<AbsenceFilters>) => {
    setFilters((prev) => ({
      ...prev,
      ...partial,
    }));
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilters(createDefaultFilters(fixedEmployeeId ?? "all"));
  }, [fixedEmployeeId]);

  return (
    <>
      {!hidePageTitle && (
        <PageTitle
          breadcrumbs={breadcrumbs}
          title={t("serviceModulesPage.absencePage.title")}
        />
      )}
      <AbsenceContainer>
        <AbsenceTableCard>
          <Stack direction={{ xs: "column", md: "row" }} sx={{ justifyContent: "space-between", gap: 2 }}>
            <Typography variant="h6">
              {t("serviceModulesPage.absencePage.filters.title")}
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              {!hideEmployeeFilter && (
                <TextField
                  select
                  label={t("serviceModulesPage.absencePage.filters.employee")}
                  value={filters.employeeUserId}
                  onChange={(event) =>
                    handleFiltersChange({ employeeUserId: event.target.value as string })
                  }
                  sx={{ minWidth: 200 }}
                  disabled={isLoadingEmployees && employeeFilterOptions.length === 0}
                >
                  <MenuItem value="all">
                    {t("serviceModulesPage.timetrackingPage.companyTable.employeeAll")}
                  </MenuItem>
                  {employeeFilterOptions.map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              )}
              <DatePicker
                label={t("serviceModulesPage.timetrackingPage.companyTable.startFilter")}
                value={filters.startDate}
                onChange={(value) => handleFiltersChange({ startDate: value })}
                format={dateFormat}
              />
              <DatePicker
                label={t("serviceModulesPage.timetrackingPage.companyTable.endFilter")}
                value={filters.endDate}
                onChange={(value) => handleFiltersChange({ endDate: value })}
                minDate={filters.startDate ?? undefined}
                format={dateFormat}
              />
              <GeneralButton appVariant="ghost" onClick={handleClearFilters}>
                {t("serviceModulesPage.timetrackingPage.companyTable.clearFilters")}
              </GeneralButton>
            </Stack>
          </Stack>
        </AbsenceTableCard>
        <AbsenceTable
          paginatedData={paginatedAbsences}
          isLoading={isTableLoading}
          updatingId={updatingId}
          onUpdateStatus={handleUpdateStatus}
        />
        {!hideCalendar && (
          <AbsenceCalendar
            getEmployeeName={getEmployeeName}
            serviceModuleId={serviceModuleId}
            employeeUserId={filters.employeeUserId === "all" ? undefined : filters.employeeUserId}
          />
        )}
      </AbsenceContainer>
    </>
  );
};

const fetchPaginatedAbsencesThunk = createAsyncThunk<
  PaginationResult<AbsenceEntry>,
  PaginationParameters & {
    serviceModuleId: number;
    employeeUserId?: string;
    startDate?: string;
    endDate?: string;
  },
  { dispatch: AppDispatch }
>(
  "absence/fetchPaginated",
  async ({ serviceModuleId, ...pagination }, { dispatch }) => {
    const result = await handleRequest(
      dispatch,
      "indirect",
      () => absenceApi.getCompanyAbsencesPaginated(serviceModuleId, pagination)
    );

    return result.success() && result.result
      ? result.result
      : { items: [], totalCount: 0 };
  }
);

export default AbsencePage;
